import { useEffect } from "react";

/** Rest position — left of hero copy (small circular point). Adjust here only. */
export const DEFAULT_LIGHT_POSITION = {
  x: 43,
  y: 42,
};

const LERP = 0.14;
const EPSILON = 0.04;

function clampPercent(value) {
  return Math.min(100, Math.max(0, value));
}

function applyLightPosition(node, x, y, active) {
  node.style.setProperty("--ds-hero-light-x", `${x}%`);
  node.style.setProperty("--ds-hero-light-y", `${y}%`);
  node.style.setProperty("--ds-hero-light-active", active ? "1" : "0");
}

function applyDefaultCssVars(node) {
  node.style.setProperty("--ds-hero-light-default-x", `${DEFAULT_LIGHT_POSITION.x}%`);
  node.style.setProperty("--ds-hero-light-default-y", `${DEFAULT_LIGHT_POSITION.y}%`);
}

/**
 * Smooth moon spotlight that follows the pointer inside the hero sticky frame.
 * On mouseleave, eases back to DEFAULT_LIGHT_POSITION (not the last cursor spot).
 * Updates CSS variables only — no React re-renders.
 */
export function useMainHeroMoonLight(stickyRef, enabled = true) {
  useEffect(() => {
    if (!enabled) return undefined;

    const node = stickyRef.current;
    if (!node) return undefined;

    const finePointer = window.matchMedia("(hover: hover) and (pointer: fine)");
    applyDefaultCssVars(node);

    if (!finePointer.matches) {
      applyLightPosition(node, DEFAULT_LIGHT_POSITION.x, DEFAULT_LIGHT_POSITION.y, 0);
      return undefined;
    }

    let rafId = 0;
    let targetX = DEFAULT_LIGHT_POSITION.x;
    let targetY = DEFAULT_LIGHT_POSITION.y;
    let currentX = DEFAULT_LIGHT_POSITION.x;
    let currentY = DEFAULT_LIGHT_POSITION.y;
    let active = 0;

    const tick = () => {
      currentX += (targetX - currentX) * LERP;
      currentY += (targetY - currentY) * LERP;

      applyLightPosition(node, currentX, currentY, active);

      const settling =
        Math.abs(targetX - currentX) > EPSILON || Math.abs(targetY - currentY) > EPSILON;
      rafId = settling ? requestAnimationFrame(tick) : 0;
    };

    const schedule = () => {
      if (!rafId) rafId = requestAnimationFrame(tick);
    };

    const onMove = (event) => {
      const rect = node.getBoundingClientRect();
      if (!rect.width || !rect.height) return;

      targetX = clampPercent(((event.clientX - rect.left) / rect.width) * 100);
      targetY = clampPercent(((event.clientY - rect.top) / rect.height) * 100);
      active = 1;
      schedule();
    };

    const onLeave = () => {
      targetX = DEFAULT_LIGHT_POSITION.x;
      targetY = DEFAULT_LIGHT_POSITION.y;
      active = 0;
      schedule();
    };

    applyLightPosition(node, currentX, currentY, active);
    node.addEventListener("mousemove", onMove, { passive: true });
    node.addEventListener("mouseleave", onLeave);

    return () => {
      node.removeEventListener("mousemove", onMove);
      node.removeEventListener("mouseleave", onLeave);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [enabled, stickyRef]);
}
